import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';

export interface ToggleOptions {
  /** Control names to exclude from toggling (e.g., ['existingMerchant']) */
  skip?: string[];
}

/**
 * Reusable toggler for FLAT forms (no nested groups/arrays).
 * - Remembers which top-level controls started disabled.
 * - Disables only enabled controls.
 * - Re-enables only those not disabled by default.
 */
@Injectable({ providedIn: 'root' })
export class FormToggleService {
  /** Per-form registry of controls that were disabled initially */
  private readonly initiallyDisabled = new WeakMap<FormGroup, Set<string>>();

  /**
   * Call once after building the form (and after adding any dynamic controls).
   * Records which top-level controls started disabled.
   */
  captureInitiallyDisabled<T extends { [K in keyof T]: AbstractControl }>(
    form: FormGroup<T>
  ): void {
    const disabled = new Set<string>();
    // Object.keys returns string[], but we only support *top-level* keys here
    (Object.keys(form.controls) as Array<keyof T & string>).forEach((key) => {
      const ctrl = form.controls[key];
      if (ctrl.disabled) disabled.add(key);
    });
    this.initiallyDisabled.set(form, disabled);
    // console.log('this.initiallyDisabled', this.initiallyDisabled);
  }

  /**
   * Toggle the *flat* form on/off.
   * - When disabling: only touches controls that are currently enabled.
   * - When enabling: never enables controls that were disabled at creation.
   */
  setDisabled<T extends { [K in keyof T]: AbstractControl }>(
    form: FormGroup<T>,
    disabled: boolean,
    options: ToggleOptions = {}
  ): void {
    const skip = new Set(options.skip ?? []);
    const initially = this.initiallyDisabled.get(form) ?? new Set<string>();

    (Object.keys(form.controls) as Array<keyof T & string>).forEach((key) => {
      if (skip.has(key)) return;

      const ctrl = form.controls[key];

      // Only operate on FormControls at top-level (flat form assumption)
      if (!(ctrl instanceof FormControl)) return;

      if (disabled) {
        if (ctrl.enabled) ctrl.disable({ emitEvent: false });
      } else {
        if (!initially.has(key) && ctrl.disabled) {
          ctrl.enable({ emitEvent: false });
        }
      }
    });
  }
}
